import { assertPublicSkuCatalog, isPublicSku } from './marketing/landingPricingSource';
import type { LandingStorySnapshot } from './landingStorySnapshot.types';

import { apiClient } from '@/api/client';

export type {
  LandingAlternativeSummary,
  LandingAvailability,
  LandingFirstPicture,
  LandingMissingField,
  LandingSourceRecord,
  LandingStorySnapshot,
} from './landingStorySnapshot.types';
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyText = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isNullableText = (value: unknown): value is string | null =>
  value === null || isNonEmptyText(value);

const isFiniteNumberOrNull = (value: unknown): boolean =>
  value === null || (typeof value === 'number' && Number.isFinite(value));

const SOURCE_RECORD_STATUSES = [
  'available',
  'stale',
  'unavailable',
  // The source we have not finished reading yet -- distinct from
  // "unavailable", which is a verdict about the source itself.
  'pending',
] as const;

const isSourceRecord = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyText(value.sourceId) &&
  isNonEmptyText(value.sourceName) &&
  isNullableText(value.observedAt) &&
  isNonEmptyText(value.snapshotAt) &&
  SOURCE_RECORD_STATUSES.includes(value.status as (typeof SOURCE_RECORD_STATUSES)[number]) &&
  (value.status !== 'stale' || isNonEmptyText(value.observedAt)) &&
  isNonEmptyText(value.immutableRef);

const isMissingField = (value: unknown): boolean =>
  isRecord(value) && isNonEmptyText(value.field) && isNonEmptyText(value.reason);

const isPublicStand = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyText(value.standId) &&
  isNullableText(value.sourceDate) &&
  isRecord(value.fields);

const hasSnapshotIdentity = (value: Record<string, unknown>): boolean =>
  value.snapshotVersion === 'landing-story.v1' &&
  isNonEmptyText(value.generatedAt) &&
  isNonEmptyText(value.immutableContextRef) &&
  isNonEmptyText(value.propertyId) &&
  value.freeForestAllowance === 1;

const hasPropertyIdentity = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyText(value.propertyId) &&
  isNonEmptyText(value.displayPropertyId) &&
  isNullableText(value.municipality) &&
  typeof value.parcelsCount === 'number';

const hasFirstPictureGeometry = (value: Record<string, unknown>): boolean =>
  (value.boundaryGeometry === null || isRecord(value.boundaryGeometry)) &&
  isFiniteNumberOrNull(value.areaHa) &&
  isFiniteNumberOrNull(value.mappedStandCount) &&
  Array.isArray(value.mappedStandRefs) &&
  value.mappedStandRefs.every(isNonEmptyText) &&
  (value.structureSummary === null || isRecord(value.structureSummary));

const hasRoughValueCoverage = (value: Record<string, unknown>): boolean =>
  Number.isInteger(value.valuedStandCount) &&
  Number.isInteger(value.totalStandCount) &&
  Number.isInteger(value.valuedStrataCount) &&
  Number.isInteger(value.totalStrataCount) &&
  (value.valuedStandCount as number) >= 0 &&
  (value.totalStandCount as number) >= (value.valuedStandCount as number) &&
  (value.valuedStrataCount as number) >= 0 &&
  (value.totalStrataCount as number) >= (value.valuedStrataCount as number);

const hasValuePriceSource = (value: unknown): boolean =>
  isRecord(value) &&
  value.sourceId === 'luke.timber-trade-statistics' &&
  isNonEmptyText(value.sourceName) &&
  isNullableText(value.observedAt);

const hasValueAccuracySource = (value: unknown): boolean =>
  isRecord(value) &&
  value.sourceId === 'luke-metsakeskus.public-inventory-accuracy' &&
  isNonEmptyText(value.sourceName) &&
  typeof value.lowerPercent === 'number' &&
  Number.isFinite(value.lowerPercent) &&
  value.lowerPercent < 0 &&
  typeof value.upperPercent === 'number' &&
  Number.isFinite(value.upperPercent) &&
  value.upperPercent > 0;

const hasAvailableRoughValue = (value: Record<string, unknown>): boolean =>
  (value.status === 'available' || value.status === 'partial') &&
  typeof value.lowEur === 'number' &&
  Number.isFinite(value.lowEur) &&
  value.lowEur > 0 &&
  typeof value.highEur === 'number' &&
  Number.isFinite(value.highEur) &&
  value.highEur >= value.lowEur &&
  hasValuePriceSource(value.priceSource) &&
  hasValueAccuracySource(value.accuracySource) &&
  value.missingReason === null;

const hasUnavailableRoughValue = (value: Record<string, unknown>): boolean =>
  value.status === 'unavailable' &&
  value.lowEur === null &&
  value.highEur === null &&
  value.priceSource === null &&
  value.accuracySource === null &&
  isNonEmptyText(value.missingReason);

const hasRoughValueRange = (value: unknown): boolean => {
  if (!isRecord(value) || value.currency !== 'EUR' || value.basis !== 'public_modelled') {
    return false;
  }
  return (
    hasRoughValueCoverage(value) &&
    (hasAvailableRoughValue(value) || hasUnavailableRoughValue(value))
  );
};

const hasFirstPictureEvidence = (value: Record<string, unknown>): boolean =>
  Array.isArray(value.publicStands) &&
  value.publicStands.every(isPublicStand) &&
  Array.isArray(value.sourceRecords) &&
  value.sourceRecords.length >= 2 &&
  value.sourceRecords.every(isSourceRecord) &&
  Array.isArray(value.missingFields) &&
  value.missingFields.every(isMissingField) &&
  isNonEmptyText(value.sourceDigest) &&
  Array.isArray(value.comparableFieldNames) &&
  value.comparableFieldNames.every(isNonEmptyText);

const FIRST_PICTURE_STATUSES = ['available', 'partial', 'stale', 'unavailable', 'pending'] as const;

// A pending picture may not carry any fact at all. Anything else would let a
// half-built snapshot render as if it had been sourced.
const hasPendingAbsence = (value: Record<string, unknown>): boolean =>
  value.status !== 'pending' ||
  (value.boundaryGeometry === null &&
    value.areaHa === null &&
    value.mappedStandCount === null &&
    value.structureSummary === null &&
    Array.isArray(value.publicStands) &&
    value.publicStands.length === 0 &&
    isRecord(value.roughValueRange) &&
    value.roughValueRange.status === 'unavailable');

const hasStaleSourceRecord = (value: Record<string, unknown>): boolean =>
  value.status !== 'stale' ||
  (Array.isArray(value.sourceRecords) &&
    value.sourceRecords.some((source) => isRecord(source) && source.status === 'stale'));

const hasFirstPictureContract = (value: unknown): boolean =>
  isRecord(value) &&
  FIRST_PICTURE_STATUSES.includes(value.status as (typeof FIRST_PICTURE_STATUSES)[number]) &&
  hasPendingAbsence(value) &&
  hasPropertyIdentity(value.identity) &&
  hasFirstPictureGeometry(value) &&
  hasRoughValueRange(value.roughValueRange) &&
  hasFirstPictureEvidence(value) &&
  hasStaleSourceRecord(value);

const containsForbiddenDecisionClaim = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(containsForbiddenDecisionClaim);
  if (!isRecord(value)) return false;
  if (['rank', 'recommended', 'winner'].some((key) => key in value)) {
    return true;
  }
  return Object.values(value).some(containsForbiddenDecisionClaim);
};

const hasAlternativeIdentity = (value: Record<string, unknown>): boolean =>
  isNonEmptyText(value.alternativeId) &&
  isNonEmptyText(value.name) &&
  isNullableText(value.sourcePlanId) &&
  isNonEmptyText(value.sourcePlanType) &&
  isNonEmptyText(value.generatedAt) &&
  isNonEmptyText(value.updatedAt) &&
  isNullableText(value.sourceSnapshotRef);

const hasOperationSummary = (value: unknown): boolean =>
  value === null ||
  (Array.isArray(value) &&
    value.length <= 5 &&
    value.every(
      (operation) =>
        isRecord(operation) &&
        isNullableText(operation.standId) &&
        isNullableText(operation.operationType) &&
        isFiniteNumberOrNull(operation.year),
    ));

const isAlternative = (value: unknown): boolean =>
  isRecord(value) &&
  hasAlternativeIdentity(value) &&
  hasOperationSummary(value.operationSummary) &&
  isRecord(value.economicsSummary) &&
  isRecord(value.timingSummary) &&
  isRecord(value.preferenceDimensions) &&
  !containsForbiddenDecisionClaim(value);

const hasPlanningContract = (value: unknown): boolean =>
  isRecord(value) &&
  ['available', 'partial', 'unavailable', 'locked'].includes(String(value.status)) &&
  Array.isArray(value.alternatives) &&
  value.alternatives.length <= 2 &&
  value.alternatives.every(isAlternative) &&
  isNullableText(value.missingReason);

const hasOwnerContextContract = (value: unknown): boolean =>
  isRecord(value) &&
  ['available', 'partial', 'unavailable', 'locked'].includes(String(value.status)) &&
  isNullableText(value.missingReason) &&
  (value.access === null || isRecord(value.access)) &&
  isNullableText(value.accessRef);

const hasMeasurementsContract = (value: unknown): boolean =>
  isRecord(value) &&
  (value.status === 'available' || value.status === 'unavailable') &&
  Array.isArray(value.stands) &&
  value.stands.every(
    (stand) =>
      isRecord(stand) &&
      isNonEmptyText(stand.standId) &&
      isNonEmptyText(stand.measurementSnapshotRef) &&
      isNonEmptyText(stand.measurementSource) &&
      stand.readinessStatus === 'ready' &&
      isNonEmptyText(stand.derivedAt) &&
      Array.isArray(stand.fieldChanges) &&
      stand.fieldChanges.every(
        (change) =>
          isRecord(change) &&
          isNonEmptyText(change.field) &&
          ['unavailable', 'supplemented', 'unchanged', 'replaced'].includes(String(change.kind)),
      ),
  ) &&
  Array.isArray(value.unavailableSnapshots) &&
  value.unavailableSnapshots.every(
    (snapshot) =>
      isRecord(snapshot) &&
      isNonEmptyText(snapshot.snapshotRef) &&
      isNonEmptyText(snapshot.standId) &&
      isNonEmptyText(snapshot.status) &&
      isNonEmptyText(snapshot.missingReason),
  ) &&
  isNullableText(value.missingReason);

const hasOfferExtractionContract = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyText(value.status) &&
  isNullableText(value.jobRef) &&
  Array.isArray(value.documents) &&
  value.documents.every(
    (document) =>
      isRecord(document) &&
      isNonEmptyText(document.documentRef) &&
      isNonEmptyText(document.filename) &&
      isNonEmptyText(document.parseStatus),
  ) &&
  Array.isArray(value.fields) &&
  value.fields.every(
    (field) =>
      isRecord(field) &&
      Object.keys(field).length === 2 &&
      isNonEmptyText(field.field) &&
      ['confirmed', 'needs_confirmation'].includes(String(field.status)),
  ) &&
  isNullableText(value.updatedAt) &&
  isNullableText(value.missingReason);

const hasImplementContract = (value: unknown): boolean =>
  isRecord(value) &&
  value.independenceStatementId === 'skogiq.does_not_buy_wood' &&
  hasOfferExtractionContract(value.offerExtractionStatus) &&
  Array.isArray(value.capabilities) &&
  value.capabilities.every(
    (capability) =>
      isRecord(capability) &&
      isNonEmptyText(capability.capabilityId) &&
      isNonEmptyText(capability.status) &&
      isNonEmptyText(capability.commercialBoundary) &&
      isNullableText(capability.unlockSkuId),
  ) &&
  Array.isArray(value.exportTypes) &&
  value.exportTypes.every(isNonEmptyText) &&
  (value.offerDecodeCatalogRecord === null || isPublicSku(value.offerDecodeCatalogRecord)) &&
  (value.plotPackageCatalogRecord === null || isPublicSku(value.plotPackageCatalogRecord));

const hasMonitoringContract = (value: unknown): boolean =>
  isRecord(value) &&
  ['active', 'inactive', 'locked'].includes(String(value.entitlement)) &&
  Array.isArray(value.watchRefs) &&
  value.watchRefs.every(isNonEmptyText) &&
  (value.latestServedChange === null ||
    (isRecord(value.latestServedChange) &&
      isNonEmptyText(value.latestServedChange.eventRef) &&
      isNonEmptyText(value.latestServedChange.observedAt) &&
      isNonEmptyText(value.latestServedChange.servedAt))) &&
  Array.isArray(value.evidence) &&
  value.evidence.every(
    (signal) =>
      isRecord(signal) &&
      isNonEmptyText(signal.source) &&
      isNonEmptyText(signal.observed_at) &&
      (isNonEmptyText(signal.signal) || isNonEmptyText(signal.name)),
  ) &&
  isNullableText(value.unlockSkuId) &&
  isNullableText(value.missingReason) &&
  (value.unlockCatalogRecord === null || isPublicSku(value.unlockCatalogRecord));

export function assertLandingStorySnapshot(value: unknown): LandingStorySnapshot {
  if (!isRecord(value)) throw new Error('Landing story snapshot must be an object');
  if (
    !hasSnapshotIdentity(value) ||
    !hasFirstPictureContract(value.firstPicture) ||
    !hasOwnerContextContract(value.ownerContext) ||
    !hasMeasurementsContract(value.measurements) ||
    !hasPlanningContract(value.planning) ||
    !hasImplementContract(value.implement) ||
    !hasMonitoringContract(value.monitoring)
  ) {
    throw new Error('Landing story snapshot has an invalid schema');
  }
  const catalog = assertPublicSkuCatalog(value.catalog);
  const implement = value.implement as Record<string, unknown>;
  const monitoring = value.monitoring as Record<string, unknown>;
  const matchesAuthoritativeSku = (embedded: Record<string, unknown>): boolean => {
    if (!catalog.available || !Array.isArray(embedded.unlocks)) return false;
    const embeddedUnlocks = embedded.unlocks;
    return catalog.skus.some(
      (sku) =>
        sku.id === embedded.id &&
        sku.catalogVersion === embedded.catalogVersion &&
        sku.name === embedded.name &&
        sku.priceEur === embedded.priceEur &&
        sku.currency === embedded.currency &&
        sku.cadence === embedded.cadence &&
        sku.perPlot === embedded.perPlot &&
        sku.teaser === embedded.teaser &&
        sku.includesYearOfPackage === embedded.includesYearOfPackage &&
        sku.unlocks.length === embeddedUnlocks.length &&
        sku.unlocks.every((unlock, index) => unlock === embeddedUnlocks[index]),
    );
  };
  for (const embedded of [
    implement.offerDecodeCatalogRecord,
    implement.plotPackageCatalogRecord,
    monitoring.unlockCatalogRecord,
  ]) {
    if (embedded === null) continue;
    if (!isRecord(embedded) || !matchesAuthoritativeSku(embedded)) {
      throw new Error('Landing story snapshot embeds a SKU outside the authoritative catalog');
    }
  }
  return { ...value, catalog } as unknown as LandingStorySnapshot;
}

export async function loadLandingStorySnapshot(
  propertyId: string,
  offerDecodeJobId?: string,
): Promise<LandingStorySnapshot> {
  const response = await apiClient.get<unknown>('/api/v1/landing/story-snapshot', {
    params: {
      property_id: propertyId,
      ...(offerDecodeJobId ? { offer_decode_job_id: offerDecodeJobId } : {}),
    },
    skipErrorLogging: (error: unknown) => {
      if (!isRecord(error) || !isRecord(error.response)) return false;
      return error.response.status === 404;
    },
  });
  const snapshot = assertLandingStorySnapshot(response.data);
  if (
    snapshot.propertyId !== propertyId ||
    snapshot.firstPicture.identity.propertyId !== propertyId ||
    snapshot.firstPicture.identity.displayPropertyId !== propertyId
  ) {
    throw new Error('Landing story snapshot does not match the requested property');
  }
  return snapshot;
}
